/* =====================================================
   MAMATHA TULLURI — THEME JS
   ===================================================== */

// Scroll position is left to the browser: new pages open at the top, while Back/Forward and
// reload return to where the visitor was (and #anchor links work).
/* --- SHARED HELPERS --- */
window.MT = window.MT || {};

// Escape text before it goes into innerHTML
const esc = str => String(str ?? '').replace(/[&<>"']/g, c => (
  { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
));

// Format an amount in cents with the store's own money format (Settings > General), so prices
// look the same everywhere: product page, cart drawer, search suggestions.
function mtMoney(cents) {
  // Always show the rupee symbol, whatever money format the store setting has
  const format = '₹{{amount}}';
  const withDelimiters = (value, precision = 2, thousands = ',', decimal = '.') => {
    const number = Number(value);
    if (isNaN(number)) return '0';
    const parts = (number / 100).toFixed(precision).split('.');
    const whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    return whole + (parts[1] ? decimal + parts[1] : '');
  };
  return format.replace(/\{\{\s*(\w+)\s*\}\}/, (match, name) => {
    switch (name) {
      case 'amount': return withDelimiters(cents, 2);
      case 'amount_no_decimals': return withDelimiters(cents, 0);
      case 'amount_with_comma_separator': return withDelimiters(cents, 2, '.', ',');
      case 'amount_no_decimals_with_comma_separator': return withDelimiters(cents, 0, '.', ',');
      case 'amount_no_decimals_with_space_separator': return withDelimiters(cents, 0, ' ');
      case 'amount_with_apostrophe_separator': return withDelimiters(cents, 2, "'", '.');
      default: return match;
    }
  });
}
window.mtMoney = mtMoney;

// Keep Tab inside an open drawer/modal. Returns { on, off }.
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';
function trapFocus(container) {
  function onKey(e) {
    if (e.key !== 'Tab') return;
    const items = [...container.querySelectorAll(FOCUSABLE)].filter(el => el.getClientRects().length > 0);
    if (!items.length) { e.preventDefault(); return; }
    const first = items[0];
    const last = items[items.length - 1];
    if (!container.contains(document.activeElement)) { e.preventDefault(); first.focus(); }
    else if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }
  return {
    on() { document.addEventListener('keydown', onKey); },
    off() { document.removeEventListener('keydown', onKey); }
  };
}
window.MT.trapFocus = trapFocus;

document.addEventListener('DOMContentLoaded', () => {
  initHeader();
  initMegaMenuHover();
  initTransparentHero();
  initCollectionHover();
  initHeroCarousels();
  initCartDrawer();
  initSearchModal();
  initWeddingGalleryLightbox();
  initMobileMenu();
  initAccordions();
  initMarquee();
  initWishlist();
  initScrollReveal();
  updateCartCount();
});

/* --- SCROLL REVEAL --- */
function initScrollReveal() {
  const els = document.querySelectorAll('.reveal');
  if (!els.length) return;

  if (!('IntersectionObserver' in window)) {
    els.forEach(el => el.classList.add('is-revealed'));
    return;
  }

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-revealed');
        observer.unobserve(entry.target);
      }
    });
  }, { threshold: 0.15, rootMargin: '0px 0px -60px 0px' });

  els.forEach(el => observer.observe(el));
}

/* In the theme editor a section is re-rendered when a setting changes (e.g. a video
   is added). The scroll observer only ran on page load, so the fresh markup would stay
   invisible — reveal it straight away. */
document.addEventListener('shopify:section:load', e => {
  e.target.querySelectorAll('.reveal').forEach(el => el.classList.add('is-revealed'));
});

/* Guards against a mega menu reopening via a "phantom" mouseenter when the
   header slides back under a stationary cursor right after a scroll (the
   header transition moves the nav link back under the pointer without the
   user actually moving their mouse). Set by initHeader() on every scroll. */
let suppressMegaHoverUntil = 0;

/* --- MEGA MENU HOVER (JS-driven so cursor can travel from nav link to fixed panel) --- */
function initMegaMenuHover() {
  document.querySelectorAll('.has-mega').forEach(navItem => {
    const mega = navItem.querySelector('.header__mega');
    if (!mega) return;

    let hideTimer = null;
    const trigger = navItem.querySelector('.header__nav-link');
    trigger?.setAttribute('aria-haspopup', 'true');
    trigger?.setAttribute('aria-expanded', 'false');

    // The ~50 menu pictures are only fetched the first time their menu is opened
    function loadMegaImages() {
      mega.querySelectorAll('img[data-mega-src]').forEach(img => {
        img.src = img.dataset.megaSrc;
        img.removeAttribute('data-mega-src');
      });
    }

    function openMega(e) {
      const viaKeyboard = e && e.type === 'focusin';
      if (!viaKeyboard && Date.now() < suppressMegaHoverUntil) return;
      clearTimeout(hideTimer);
      loadMegaImages();
      trigger?.setAttribute('aria-expanded', 'true');
      // Close all other open megas
      document.querySelectorAll('.header__mega.is-open').forEach(m => {
        if (m !== mega) m.classList.remove('is-open');
      });
      // Recompute position at open time rather than trusting whatever the
      // last scroll/resize event left it at — a stale value (e.g. sampled
      // mid-transition while the header was hiding/showing) would render
      // the panel overlapping the header instead of below it.
      const header = document.getElementById('site-header');
      if (header) mega.style.top = `${header.getBoundingClientRect().bottom}px`;
      mega.classList.add('is-open');
    }

    function closeMega() {
      hideTimer = setTimeout(() => {
        mega.classList.remove('is-open');
        trigger?.setAttribute('aria-expanded', 'false');
      }, 150);
    }

    navItem.addEventListener('mouseenter', openMega);
    navItem.addEventListener('mouseleave', closeMega);
    mega.addEventListener('mouseenter', openMega);
    mega.addEventListener('mouseleave', closeMega);

    // Keyboard: the menu opens while focus is on the link or inside the panel, and Escape closes it
    navItem.addEventListener('focusin', openMega);
    navItem.addEventListener('focusout', e => {
      if (!navItem.contains(e.relatedTarget)) closeMega();
    });
    navItem.addEventListener('keydown', e => {
      if (e.key !== 'Escape' || !mega.classList.contains('is-open')) return;
      mega.classList.remove('is-open');
      trigger?.setAttribute('aria-expanded', 'false');
      trigger?.focus();
    });
    // "World of MT" is a button, so Enter / Space toggles it
    if (trigger && trigger.tagName === 'BUTTON') {
      trigger.addEventListener('click', () => {
        if (mega.classList.contains('is-open')) {
          mega.classList.remove('is-open');
          trigger.setAttribute('aria-expanded', 'false');
        } else {
          openMega({ type: 'focusin' });
        }
      });
    }
  });
}

/* --- COLLECTIONS HOVER PREVIEW --- */
function initCollectionHover() {
  const menu = document.getElementById('mega-collections');
  if (!menu) return;

  const links = menu.querySelectorAll('.header__col-link');
  const previews = menu.querySelectorAll('.header__col-preview');

  links.forEach(link => {
    link.addEventListener('mouseenter', () => {
      const col = link.dataset.col;
      links.forEach(l => l.classList.remove('is-active'));
      previews.forEach(p => p.classList.remove('is-active'));
      link.classList.add('is-active');
      menu.querySelector(`.header__col-preview[data-col="${col}"]`)?.classList.add('is-active');
    });
  });
}

/* --- TRANSPARENT HERO — header is fixed so hero is a clean 100vh from top 0 --- */
function initTransparentHero() {
  // Nothing needed: header is position:fixed so it doesn't take document space.
  // Hero is naturally 100vh starting at document top, transparent nav floats over it.
}

/* --- HEADER SCROLL --- */
function initHeader() {
  const header = document.getElementById('site-header');
  if (!header) return;

  function updateMegaTop() {
    const bottom = header.getBoundingClientRect().bottom;
    document.querySelectorAll('.header__mega').forEach(m => {
      m.style.top = `${bottom}px`;
    });
  }

  let lastScrollY = window.scrollY;
  let ticking = false;

  const onScroll = () => {
    if (!ticking) {
      window.requestAnimationFrame(() => {
        const currentY = window.scrollY;
        const scrolledDown = currentY > lastScrollY;
        const pastThreshold = currentY > 80;

        header.classList.toggle('scrolled', currentY > 20);

        if (scrolledDown && pastThreshold) {
          header.classList.add('header--hidden');
        } else {
          header.classList.remove('header--hidden');
        }

        // A mega menu left open during a scroll can get stranded: the
        // header hide/show transform doesn't fire mouseleave, so the panel
        // never closes and ends up repositioned over the page. Close on
        // any scroll instead of relying on hover state surviving it, and
        // briefly suppress re-opening so the header sliding back under a
        // stationary cursor can't trigger a phantom mouseenter.
        document.querySelectorAll('.header__mega.is-open').forEach(m => m.classList.remove('is-open'));
        suppressMegaHoverUntil = Date.now() + 400;

        lastScrollY = currentY;
        ticking = false;
        updateMegaTop();
      });
      ticking = true;
    }
  };

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', updateMegaTop, { passive: true });
  onScroll();
  updateMegaTop();
}

/* --- HERO CAROUSELS --- */
function initHeroCarousels() {
  document.querySelectorAll('.hero').forEach(hero => {
    const id = hero.id.replace('hero-', '');
    const slides = hero.querySelectorAll('.hero__slide');
    const dots = hero.querySelectorAll('.hero__dot');
    const prevBtn = hero.querySelector('.hero__arrow--prev');
    const nextBtn = hero.querySelector('.hero__arrow--next');

    if (slides.length <= 1) return;

    let current = 0;
    let timer = null;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const autoplay = hero.dataset.autoplay === 'true' && !reduceMotion;
    const interval = parseInt(hero.dataset.interval) || 6000;

    function goTo(idx) {
      slides[current].classList.add('hero__slide--hidden');
      dots[current]?.classList.remove('is-active');
      dots[current]?.setAttribute('aria-selected', 'false');
      current = (idx + slides.length) % slides.length;
      slides[current].classList.remove('hero__slide--hidden');
      dots[current]?.classList.add('is-active');
      dots[current]?.setAttribute('aria-selected', 'true');
    }

    function startTimer() {
      if (!autoplay) return;
      clearInterval(timer);
      timer = setInterval(() => goTo(current + 1), interval);
    }
    if (autoplay) {
      hero.addEventListener('mouseenter', () => clearInterval(timer));
      hero.addEventListener('mouseleave', startTimer);
    }

    prevBtn?.addEventListener('click', () => { goTo(current - 1); startTimer(); });
    nextBtn?.addEventListener('click', () => { goTo(current + 1); startTimer(); });
    dots.forEach((dot, i) => dot.addEventListener('click', () => { goTo(i); startTimer(); }));

    // Keyboard
    hero.addEventListener('keydown', e => {
      if (e.key === 'ArrowLeft') { goTo(current - 1); startTimer(); }
      if (e.key === 'ArrowRight') { goTo(current + 1); startTimer(); }
    });

    // Touch/swipe
    let touchStartX = 0;
    hero.addEventListener('touchstart', e => { touchStartX = e.touches[0].clientX; }, { passive: true });
    hero.addEventListener('touchend', e => {
      const diff = touchStartX - e.changedTouches[0].clientX;
      if (Math.abs(diff) > 50) {
        diff > 0 ? goTo(current + 1) : goTo(current - 1);
        startTimer();
      }
    });

    startTimer();
  });
}

/* --- CART DRAWER --- */
async function updateCartCount() {
  try {
    const res = await fetch('/cart.js');
    const cart = await res.json();
    const count = document.getElementById('cart-count');
    if (count) {
      count.textContent = cart.item_count > 0 ? cart.item_count : '';
    }
  } catch (e) { /* silently fail */ }
}

function initCartDrawer() {
  const drawer = document.getElementById('cart-drawer');
  const overlay = document.getElementById('cart-overlay');
  const openBtn = document.getElementById('cart-open');
  const closeBtn = document.getElementById('cart-close');
  if (!drawer) return;

  const trap = trapFocus(drawer);
  let returnFocusTo = null;
  drawer.inert = true; // closed: nothing inside can be tabbed to

  function openCart() {
    returnFocusTo = document.activeElement;
    drawer.inert = false;
    drawer.classList.add('is-open');
    overlay.classList.add('is-visible');
    drawer.setAttribute('aria-hidden', 'false');
    closeBtn?.focus();
    trap.on();
    document.body.style.overflow = 'hidden';
    loadCartContents();
  }

  function closeCart() {
    drawer.classList.remove('is-open');
    overlay.classList.remove('is-visible');
    drawer.setAttribute('aria-hidden', 'true');
    drawer.inert = true;
    trap.off();
    document.body.style.overflow = '';
    (returnFocusTo && document.contains(returnFocusTo) ? returnFocusTo : openBtn)?.focus();
  }

  // Quantity buttons (rendered by loadCartContents)
  document.getElementById('cart-drawer-body')?.addEventListener('click', e => {
    const btn = e.target.closest('[data-qty-key]');
    if (btn) updateCartItem(btn.dataset.qtyKey, parseInt(btn.dataset.qty, 10));
  });

  openBtn?.addEventListener('click', openCart);
  closeBtn?.addEventListener('click', closeCart);
  overlay?.addEventListener('click', closeCart);

  document.addEventListener('keydown', e => {
    if (e.key === 'Escape' && drawer.classList.contains('is-open')) closeCart();
  });

  // Listen for add-to-cart events from product pages
  document.addEventListener('cart:open', openCart);
}

async function loadCartContents() {
  const body = document.getElementById('cart-drawer-body');
  const subtotalEl = document.getElementById('cart-subtotal');
  if (!body) return;

  try {
    const res = await fetch('/cart.js');
    const cart = await res.json();

    const drawerFooter = document.querySelector('.cart-drawer__footer');
    const badge = document.getElementById('cart-count');

    if (cart.item_count === 0) {
      body.innerHTML = `
        <div class="cart-drawer__empty">
          <p class="cart-drawer__empty-text">Your bag is empty</p>
          <a href="/collections/all" class="btn btn--outline">Start Shopping</a>
        </div>`;
      if (subtotalEl) subtotalEl.textContent = '';
      if (badge) badge.textContent = '';
      if (drawerFooter) drawerFooter.hidden = true;
      return;
    }
    if (drawerFooter) drawerFooter.hidden = false;

    body.innerHTML = cart.items.map(item => {
      const title = esc(item.product_title);
      const variantText = esc([
        (item.variant_title && item.variant_title !== 'Default Title') ? item.variant_title : '',
        (item.properties && item.properties.Size) ? 'Size: ' + item.properties.Size : ''
      ].filter(Boolean).join(' · '));
      const image = item.image
        ? `<img src="${esc(item.image)}" alt="${title}" class="cart-item__image" width="80" loading="lazy">`
        : `<div class="cart-item__image cart-item__image--empty" aria-hidden="true"></div>`;
      return `
      <div class="cart-item" data-key="${esc(item.key)}">
        ${image}
        <div class="cart-item__details">
          <p class="cart-item__name">${title}</p>
          <p class="cart-item__variant">${variantText}</p>
          <div class="cart-item__price-row">
            <span class="cart-item__price">${esc(mtMoney(item.final_line_price))}</span>
            <div class="cart-item__qty">
              <button type="button" data-qty-key="${esc(item.key)}" data-qty="${item.quantity - 1}" aria-label="Decrease quantity of ${title}">−</button>
              <span>${item.quantity}</span>
              <button type="button" data-qty-key="${esc(item.key)}" data-qty="${item.quantity + 1}" aria-label="Increase quantity of ${title}">+</button>
            </div>
          </div>
        </div>
      </div>`;
    }).join('');

    if (subtotalEl) {
      subtotalEl.textContent = mtMoney(cart.total_price);
    }

    // Update count badge
    const countEl = document.getElementById('cart-count');
    if (countEl) countEl.textContent = cart.item_count > 0 ? cart.item_count : '';

  } catch (e) {
    body.innerHTML = '<p style="padding: 20px; color: var(--color-text-muted); font-size: 0.85rem;">Could not load cart.</p>';
  }
}

async function updateCartItem(key, qty) {
  try {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: key, quantity: qty })
    });
    await res.json();
  } catch (e) { /* the reload below shows the cart as it really is */ }
  loadCartContents();
}

// Expose globally for inline handlers
window.updateCartItem = updateCartItem;

/* --- ADD TO CART --- */
document.addEventListener('submit', async function(e) {
  const form = e.target;
  if (!form.matches('[action="/cart/add"], [data-add-to-cart]')) return;
  e.preventDefault();

  const btn = form.querySelector('[type="submit"]');
  const originalText = btn?.textContent;
  const errorEl = document.getElementById('cart-error');
  if (errorEl) errorEl.hidden = true;
  if (btn) {
    btn.disabled = true;
    btn.textContent = 'Adding...';
  }

  try {
    const formData = new FormData(form);
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Accept': 'application/json' },
      body: formData
    });

    if (!res.ok) {
      // Shopify explains why (e.g. "All 1 of this item are already in your cart")
      const info = await res.json().catch(() => null);
      const err = new Error(info?.description || info?.message || 'Add to cart failed');
      err.userMessage = info?.description || '';
      throw err;
    }

    const added = await res.json().catch(() => null);
    document.dispatchEvent(new CustomEvent('cart:added', { detail: added }));
    document.dispatchEvent(new CustomEvent('cart:open'));

    if (btn) {
      btn.textContent = 'Added!';
      setTimeout(() => {
        btn.textContent = originalText;
        btn.disabled = false;
      }, 1500);
    }
  } catch (err) {
    if (btn) {
      btn.textContent = originalText;
      btn.disabled = false;
    }
    if (errorEl) {
      errorEl.textContent = err.userMessage || 'Sorry, we could not add this to your bag. Please try again or contact us on WhatsApp.';
      errorEl.hidden = false;
    }
  }
});

/* --- SEARCH MODAL --- */
function initSearchModal() {
  const modal = document.getElementById('search-modal');
  const openBtn = document.getElementById('search-open');
  const closeBtn = document.getElementById('search-close');
  if (!modal) return;

  const trap = trapFocus(modal);
  let returnFocusTo = null;
  modal.inert = true; // closed: nothing inside can be tabbed to

  function openSearch() {
    returnFocusTo = document.activeElement;
    modal.inert = false;
    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    trap.on();
    modal.querySelector('input')?.focus();
  }

  function closeSearch() {
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    modal.inert = true;
    trap.off();
    document.body.style.overflow = '';
    controller?.abort();
    hideSuggest();
    (returnFocusTo && document.contains(returnFocusTo) ? returnFocusTo : openBtn)?.focus();
  }

  /* Live suggestions — Shopify's predictive search endpoint */
  const form = modal.querySelector('.search-modal__form');
  const input = modal.querySelector('.search-modal__input');
  const box = document.getElementById('search-suggest');
  let timer = null;
  let controller = null;

  const money = v => {
    const n = parseFloat(v);
    return isNaN(n) ? '' : mtMoney(Math.round(n * 100));
  };
  const hideSuggest = () => { if (box) { box.hidden = true; box.innerHTML = ''; } };

  function renderSuggest(q, data) {
    const res = data?.resources?.results || {};
    const products = res.products || [];
    const collections = res.collections || [];
    if (!products.length && !collections.length) {
      box.innerHTML = '<p class="search-suggest__empty">No matches for &ldquo;' + esc(q) + '&rdquo;</p>';
      box.hidden = false;
      return;
    }
    let html = '';
    if (collections.length) {
      html += '<p class="search-suggest__heading">Collections</p>';
      html += collections.map(c =>
        '<a class="search-suggest__link" role="option" href="' + esc(c.url) + '">' + esc(c.title) + '</a>'
      ).join('');
    }
    if (products.length) {
      html += '<p class="search-suggest__heading">Products</p>';
      html += products.map(p => {
        const img = p.image ? '<img src="' + esc(p.image) + (String(p.image).indexOf('?') > -1 ? '&' : '?') + 'width=160" alt="" width="48" height="64">' : '<span class="search-suggest__noimg"></span>';
        return '<a class="search-suggest__item" role="option" href="' + esc(p.url) + '">' + img +
          '<span class="search-suggest__text"><span class="search-suggest__title">' + esc(p.title) + '</span>' +
          '<span class="search-suggest__price">' + esc(money(p.price)) + '</span></span></a>';
      }).join('');
    }
    html += '<a class="search-suggest__all" href="/search?type=product&q=' + encodeURIComponent(q) + '">View all results for &ldquo;' + esc(q) + '&rdquo;</a>';
    box.innerHTML = html;
    box.hidden = false;
  }

  function fetchSuggest(q) {
    controller?.abort();
    controller = new AbortController();
    const url = '/search/suggest.json?q=' + encodeURIComponent(q) +
      '&resources[type]=product,collection&resources[limit]=6&resources[options][unavailable_products]=last';
    fetch(url, { signal: controller.signal, headers: { Accept: 'application/json' } })
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(data => { if (input.value.trim() === q) renderSuggest(q, data); })
      .catch(err => { if (err?.name !== 'AbortError') hideSuggest(); });
  }

  input?.addEventListener('input', () => {
    clearTimeout(timer);
    const q = input.value.trim();
    if (q.length < 2) { controller?.abort(); hideSuggest(); return; }
    timer = setTimeout(() => fetchSuggest(q), 200);
  });

  // Arrow keys move through the suggestions; Enter on one opens it
  input?.addEventListener('keydown', e => {
    if (!box || box.hidden || (e.key !== 'ArrowDown' && e.key !== 'ArrowUp')) return;
    const links = [...box.querySelectorAll('a')];
    if (!links.length) return;
    e.preventDefault();
    const i = links.indexOf(document.activeElement);
    const next = e.key === 'ArrowDown' ? (i + 1) % links.length : (i <= 0 ? links.length - 1 : i - 1);
    links[next].focus();
  });
  box?.addEventListener('keydown', e => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    const links = [...box.querySelectorAll('a')];
    const i = links.indexOf(document.activeElement);
    e.preventDefault();
    if (e.key === 'ArrowUp' && i <= 0) { input.focus(); return; }
    links[e.key === 'ArrowDown' ? (i + 1) % links.length : i - 1].focus();
  });
  form?.addEventListener('submit', hideSuggest);

  openBtn?.addEventListener('click', openSearch);
  closeBtn?.addEventListener('click', closeSearch);
  document.addEventListener('keydown', e => {
    if (e.key === 'Escape' && modal.classList.contains('is-open')) closeSearch();
  });
  modal.addEventListener('click', e => {
    if (e.target === modal) closeSearch();
  });
}

/* --- WEDDING GALLERY LIGHTBOX --- */
function initWeddingGalleryLightbox() {
  const lightbox = document.getElementById('gallery-lightbox');
  if (!lightbox) return;

  const titleEl = document.getElementById('gallery-lightbox-title');
  const gridEl = document.getElementById('gallery-lightbox-grid');
  const closeBtn = document.getElementById('gallery-lightbox-close');
  const backdrop = lightbox.querySelector('.gallery-lightbox__backdrop');

  function openLightbox(templateId) {
    const template = document.getElementById(templateId);
    if (!template) return;
    titleEl.textContent = template.dataset.galleryTitle || '';
    gridEl.innerHTML = '';
    gridEl.appendChild(template.content.cloneNode(true));
    lightbox.classList.add('is-open');
    lightbox.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  }

  function closeLightbox() {
    lightbox.classList.remove('is-open');
    lightbox.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  document.querySelectorAll('[data-lightbox]').forEach(card => {
    card.addEventListener('click', () => openLightbox(card.dataset.lightbox));
  });

  closeBtn?.addEventListener('click', closeLightbox);
  backdrop?.addEventListener('click', closeLightbox);
  document.addEventListener('keydown', e => {
    if (e.key === 'Escape' && lightbox.classList.contains('is-open')) closeLightbox();
  });
}

/* --- MOBILE MENU --- */
function initMobileMenu() {
  const menu = document.getElementById('mobile-menu');
  const overlay = document.getElementById('mobile-menu-overlay');
  const openBtn = document.getElementById('mobile-menu-open');
  const closeBtn = document.getElementById('mobile-menu-close');
  if (!menu) return;

  const trap = trapFocus(menu);
  menu.inert = true; // closed: nothing inside can be tabbed to

  function openMenu() {
    menu.inert = false;
    menu.classList.add('is-open');
    overlay.classList.add('is-visible');
    menu.setAttribute('aria-hidden', 'false');
    openBtn?.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
    trap.on();
    (closeBtn || menu.querySelector(FOCUSABLE))?.focus();
  }

  function closeMenu() {
    menu.classList.remove('is-open');
    overlay.classList.remove('is-visible');
    menu.setAttribute('aria-hidden', 'true');
    menu.inert = true;
    trap.off();
    openBtn?.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
    openBtn?.focus();
  }

  openBtn?.addEventListener('click', openMenu);
  closeBtn?.addEventListener('click', closeMenu);
  overlay?.addEventListener('click', closeMenu);
  document.addEventListener('keydown', e => {
    if (e.key === 'Escape' && menu.classList.contains('is-open')) closeMenu();
  });

  // Mobile submenu toggles
  menu.querySelectorAll('.mobile-menu__toggle').forEach(btn => {
    btn.addEventListener('click', () => {
      const sub = btn.nextElementSibling;
      const isOpen = sub?.classList.contains('is-open');
      // Close all others
      menu.querySelectorAll('.mobile-menu__sub.is-open').forEach(s => s.classList.remove('is-open'));
      if (!isOpen && sub) sub.classList.add('is-open');
    });
  });
}

/* --- ACCORDIONS --- */
function initAccordions() {
  document.querySelectorAll('.accordion-trigger').forEach(trigger => {
    trigger.addEventListener('click', () => {
      const isOpen = trigger.classList.contains('is-open');
      const content = trigger.nextElementSibling;

      trigger.classList.toggle('is-open', !isOpen);
      content?.classList.toggle('is-open', !isOpen);
      trigger.setAttribute('aria-expanded', String(!isOpen));
    });
  });
}

/* --- MARQUEE PAUSE ON HOVER --- */
function initMarquee() {
  document.querySelectorAll('.marquee-section').forEach(section => {
    section.addEventListener('mouseenter', () => {
      section.querySelector('.marquee-track').style.animationPlayState = 'paused';
    });
    section.addEventListener('mouseleave', () => {
      section.querySelector('.marquee-track').style.animationPlayState = 'running';
    });
  });
}

/* --- WISHLIST (saved in this browser via localStorage) --- */
function initWishlist() {
  const KEY = 'mt_wishlist';
  const read = () => {
    try { return JSON.parse(localStorage.getItem(KEY)) || []; } catch (e) { return []; }
  };
  const write = list => {
    try { localStorage.setItem(KEY, JSON.stringify(list)); } catch (e) {}
  };

  const paint = btn => {
    const saved = read().includes(btn.dataset.wishlistHandle);
    btn.classList.toggle('is-active', saved);
    btn.setAttribute('aria-pressed', String(saved));
    const label = saved ? btn.dataset.labelAdded : btn.dataset.labelAdd;
    const text = btn.querySelector('.product-info__wishlist-label');
    if (text) text.textContent = label;
    else if (label) btn.setAttribute('aria-label', label);
  };

  const paintAll = () => document.querySelectorAll('[data-wishlist-toggle]').forEach(paint);
  paintAll();

  document.addEventListener('click', e => {
    const btn = e.target.closest('[data-wishlist-toggle]');
    if (!btn) return;
    e.preventDefault(); // cards sit inside a product link
    const handle = btn.dataset.wishlistHandle;
    const list = read();
    const i = list.indexOf(handle);
    if (i > -1) list.splice(i, 1); else list.push(handle);
    write(list);
    paintAll();
  });
}
